import { useState, useEffect, useMemo } from "react";
import { TrendingUp, TrendingDown, Award, BarChart2, RefreshCw, AlertCircle } from "lucide-react";
import { fetchTrades, fetchStockPortfolio } from "../api";
import type { Trade, StockHolding } from "../api";

// ─── Helpers ───────────────────────────────────────────────────────────────

function formatStrategy(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatStrikes(trade: Trade): string {
  const isPut = trade.strategy === "bull_put_spread" || trade.strategy === "sell_put";
  const leg = isPut ? "P" : "C";
  if (trade.long_strike) {
    return `$${trade.short_strike}${leg} / $${trade.long_strike}${leg}`;
  }
  return `$${trade.short_strike}${leg}`;
}

function formatExpiry(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const utc = new Date(Date.UTC(y, m - 1, d));
  return utc.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit", timeZone: "UTC" });
}

function formatDate(isoStr: string | null): string {
  if (!isoStr) return "—";
  return new Date(isoStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });
}

function pnlColor(val: number | undefined | null): string {
  if (val == null) return "#94a3b8";
  return val >= 0 ? "#4ade80" : "#f87171";
}

function statusConfig(status: string): { label: string; bg: string; color: string } {
  switch (status) {
    case "closed_profit": return { label: "PROFIT", bg: "#052e16", color: "#4ade80" };
    case "closed_loss": return { label: "LOSS", bg: "#1a0a0a", color: "#f87171" };
    case "expired_worthless": return { label: "EXPIRED", bg: "#052e16", color: "#4ade80" };
    case "assigned": return { label: "ASSIGNED", bg: "#451a03", color: "#fb923c" };
    case "rolled": return { label: "ROLLED", bg: "#1c1200", color: "#facc15" };
    default: return { label: status.toUpperCase(), bg: "#0d1526", color: "#94a3b8" };
  }
}

type Period = 30 | 90 | 365;

function filterByPeriod(trades: Trade[], period: Period): Trade[] {
  const cutoff = Date.now() - period * 86400000;
  return trades.filter((t) => {
    const closedAt = t.closed_at ? new Date(t.closed_at).getTime() : null;
    return closedAt != null && closedAt >= cutoff;
  });
}

// ─── Stat card ─────────────────────────────────────────────────────────────

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  color: string;
}

function StatCard({ icon, label, value, sub, color }: StatCardProps) {
  return (
    <div style={{
      background: "#0a1628", border: "1px solid #1e293b", borderRadius: 12,
      padding: "20px 22px", display: "flex", alignItems: "center", gap: 14,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center",
        color, flexShrink: 0,
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 9, color: "#475569", letterSpacing: 1, marginBottom: 4, fontFamily: "monospace" }}>
          {label}
        </div>
        <div style={{ fontSize: 24, fontWeight: 700, color, fontFamily: "'Space Mono', monospace" }}>
          {value}
        </div>
        {sub && (
          <div style={{ fontSize: 11, color: "#475569", marginTop: 2, fontFamily: "monospace" }}>
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── WinRateBar ────────────────────────────────────────────────────────────

function WinRateGauge({ rate }: { rate: number }) {
  const color = rate >= 70 ? "#4ade80" : rate >= 50 ? "#facc15" : "#f87171";
  const pct = Math.min(100, Math.max(0, rate));
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{
        flex: 1, height: 6, background: "#1e293b", borderRadius: 3, overflow: "hidden",
      }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 11, color, fontFamily: "monospace", width: 36, textAlign: "right" }}>
        {pct.toFixed(0)}%
      </span>
    </div>
  );
}

// ─── ClosedTradesTable ─────────────────────────────────────────────────────

const COL: React.CSSProperties = {
  padding: "12px 16px",
  fontSize: 12,
  fontFamily: "monospace",
  borderBottom: "1px solid #0d1526",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};

function ClosedTradesTable({ trades }: { trades: Trade[] }) {
  if (trades.length === 0) return null;
  return (
    <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #1e293b" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #1e293b" }}>
            {["Ticker", "Strategy", "Strikes", "Expiry", "Opened", "Closed", "Credit", "P&L", "Result"].map((h) => (
              <th key={h} style={{
                ...COL,
                color: "#475569", fontSize: 10, letterSpacing: 1, fontWeight: 600,
                background: "#0d1526", textAlign: "left",
                borderBottom: "1px solid #1e293b",
              }}>
                {h.toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {trades.map((trade, i) => {
            const sc = statusConfig(trade.status);
            const pnl = trade.realized_pnl;
            const pnlDisplay = pnl != null
              ? `${pnl >= 0 ? "+" : ""}$${pnl.toFixed(0)}`
              : "—";

            return (
              <tr
                key={trade.id}
                style={{ background: i % 2 === 0 ? "#0a1628" : "#080f1e" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#0d1526")}
                onMouseLeave={(e) => (e.currentTarget.style.background = i % 2 === 0 ? "#0a1628" : "#080f1e")}
              >
                <td style={COL}>
                  <span style={{ color: "#f1f5f9", fontWeight: 700, fontFamily: "'Space Mono', monospace", fontSize: 13 }}>
                    {trade.ticker}
                  </span>
                </td>
                <td style={COL}>
                  <span style={{ color: "#94a3b8" }}>{formatStrategy(trade.strategy)}</span>
                </td>
                <td style={COL}>
                  <span style={{ color: "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>
                    {formatStrikes(trade)}
                  </span>
                </td>
                <td style={COL}>
                  <span style={{ color: "#64748b" }}>{formatExpiry(trade.short_expiry)}</span>
                </td>
                <td style={COL}>
                  <span style={{ color: "#475569" }}>{formatDate(trade.opened_at)}</span>
                </td>
                <td style={COL}>
                  <span style={{ color: "#475569" }}>{formatDate(trade.closed_at)}</span>
                </td>
                <td style={COL}>
                  <span style={{ color: "#4ade80", fontFamily: "'Space Mono', monospace" }}>
                    ${trade.net_credit.toFixed(2)}
                  </span>
                </td>
                <td style={COL}>
                  <span style={{
                    color: pnlColor(pnl),
                    fontFamily: "'Space Mono', monospace",
                    fontWeight: 700,
                    fontSize: 13,
                  }}>
                    {pnlDisplay}
                  </span>
                </td>
                <td style={COL}>
                  <span style={{
                    padding: "3px 8px", borderRadius: 5, fontSize: 10, fontWeight: 700,
                    letterSpacing: 0.5, background: sc.bg, color: sc.color,
                  }}>
                    {sc.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── SoldStocksTable ──────────────────────────────────────────────────────

function SoldStocksTable({ holdings }: { holdings: StockHolding[] }) {
  if (holdings.length === 0) return null;
  return (
    <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #1e293b" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #1e293b" }}>
            {["Ticker", "Category", "Source", "Qty", "Buy", "Sell", "P&L", "Return", "Sold", "Result"].map((h) => (
              <th key={h} style={{
                ...COL,
                color: "#475569", fontSize: 10, letterSpacing: 1, fontWeight: 600,
                background: "#0d1526", textAlign: "left",
                borderBottom: "1px solid #1e293b",
              }}>
                {h.toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {holdings.map((h, i) => {
            const pnl = h.realized_pnl ?? 0;
            const pnlPct = h.realized_pnl_pct ?? 0;
            const isProfit = pnl >= 0;
            const sc = isProfit
              ? { label: "PROFIT", bg: "#052e16", color: "#4ade80" }
              : { label: "LOSS", bg: "#1a0a0a", color: "#f87171" };

            return (
              <tr
                key={h.id}
                style={{ background: i % 2 === 0 ? "#0a1628" : "#080f1e" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#0d1526")}
                onMouseLeave={(e) => (e.currentTarget.style.background = i % 2 === 0 ? "#0a1628" : "#080f1e")}
              >
                <td style={COL}>
                  <span style={{ color: "#f1f5f9", fontWeight: 700, fontFamily: "'Space Mono', monospace", fontSize: 13 }}>
                    {h.ticker}
                  </span>
                </td>
                <td style={COL}>
                  <span style={{ color: "#94a3b8", textTransform: "capitalize" }}>{h.category.replace(/_/g, " ")}</span>
                </td>
                <td style={COL}>
                  <span style={{
                    fontSize: 9, fontFamily: "monospace", fontWeight: 700, letterSpacing: 0.5,
                    padding: "3px 7px", borderRadius: 4,
                    background: h.is_agent_pick ? "#1d4ed815" : "#47556915",
                    color: h.is_agent_pick ? "#60a5fa" : "#64748b",
                  }}>
                    {h.is_agent_pick ? "AGENT" : "MANUAL"}
                  </span>
                </td>
                <td style={COL}>
                  <span style={{ color: "#94a3b8", fontFamily: "'Space Mono', monospace" }}>{h.quantity}</span>
                </td>
                <td style={COL}>
                  <span style={{ color: "#94a3b8", fontFamily: "'Space Mono', monospace" }}>
                    ${h.buy_price.toFixed(2)}
                  </span>
                </td>
                <td style={COL}>
                  <span style={{ color: "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>
                    ${(h.sell_price ?? 0).toFixed(2)}
                  </span>
                </td>
                <td style={COL}>
                  <span style={{
                    color: pnlColor(pnl), fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 13,
                  }}>
                    {pnl >= 0 ? "+" : ""}${pnl.toFixed(0)}
                  </span>
                </td>
                <td style={COL}>
                  <span style={{
                    color: pnlColor(pnlPct), fontFamily: "'Space Mono', monospace", fontSize: 12,
                  }}>
                    {pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(1)}%
                  </span>
                </td>
                <td style={COL}>
                  <span style={{ color: "#475569" }}>{formatDate(h.sold_at)}</span>
                </td>
                <td style={COL}>
                  <span style={{
                    padding: "3px 8px", borderRadius: 5, fontSize: 10, fontWeight: 700,
                    letterSpacing: 0.5, background: sc.bg, color: sc.color,
                  }}>
                    {sc.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── ActiveHoldingsTable ──────────────────────────────────────────────────

function ActiveHoldingsTable({ holdings, pnlFn, pnlPctFn }: {
  holdings: StockHolding[];
  pnlFn: (h: StockHolding) => number;
  pnlPctFn: (h: StockHolding) => number;
}) {
  if (holdings.length === 0) return null;
  return (
    <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #1e293b" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #1e293b" }}>
            {["Ticker", "Category", "Source", "Qty", "Avg Price", "Current", "Value", "P&L", "Return", "Signal"].map((h) => (
              <th key={h} style={{
                ...COL,
                color: "#475569", fontSize: 10, letterSpacing: 1, fontWeight: 600,
                background: "#0d1526", textAlign: "left",
                borderBottom: "1px solid #1e293b",
              }}>
                {h.toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {holdings.map((h, i) => {
            const pnl = pnlFn(h);
            const pnlPct = pnlPctFn(h);

            return (
              <tr
                key={h.id}
                style={{ background: i % 2 === 0 ? "#0a1628" : "#080f1e" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#0d1526")}
                onMouseLeave={(e) => (e.currentTarget.style.background = i % 2 === 0 ? "#0a1628" : "#080f1e")}
              >
                <td style={COL}>
                  <span style={{ color: "#f1f5f9", fontWeight: 700, fontFamily: "'Space Mono', monospace", fontSize: 13 }}>
                    {h.ticker}
                  </span>
                </td>
                <td style={COL}>
                  <span style={{ color: "#94a3b8", textTransform: "capitalize" }}>{h.category.replace(/_/g, " ")}</span>
                </td>
                <td style={COL}>
                  <span style={{
                    fontSize: 9, fontFamily: "monospace", fontWeight: 700, letterSpacing: 0.5,
                    padding: "3px 7px", borderRadius: 4,
                    background: h.is_agent_pick ? "#1d4ed815" : "#47556915",
                    color: h.is_agent_pick ? "#60a5fa" : "#64748b",
                  }}>
                    {h.is_agent_pick ? "AGENT" : "MANUAL"}
                  </span>
                </td>
                <td style={COL}>
                  <span style={{ color: "#94a3b8", fontFamily: "'Space Mono', monospace" }}>{h.quantity}</span>
                </td>
                <td style={COL}>
                  <span style={{ color: "#94a3b8", fontFamily: "'Space Mono', monospace" }}>
                    ${h.buy_price.toFixed(2)}
                  </span>
                </td>
                <td style={COL}>
                  <span style={{ color: "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>
                    ${(h.current_price ?? h.buy_price).toFixed(2)}
                  </span>
                </td>
                <td style={COL}>
                  <span style={{ color: "#e2e8f0", fontFamily: "'Space Mono', monospace", fontWeight: 600 }}>
                    ${((h.current_price ?? h.buy_price) * h.quantity).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                  </span>
                </td>
                <td style={COL}>
                  <span style={{
                    color: pnlColor(pnl), fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 13,
                  }}>
                    {pnl >= 0 ? "+" : ""}${pnl.toFixed(0)}
                  </span>
                </td>
                <td style={COL}>
                  <span style={{
                    color: pnlColor(pnlPct), fontFamily: "'Space Mono', monospace", fontSize: 12,
                  }}>
                    {pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(1)}%
                  </span>
                </td>
                <td style={COL}>
                  <span style={{
                    padding: "3px 8px", borderRadius: 5, fontSize: 10, fontWeight: 700,
                    letterSpacing: 0.5,
                    background: h.signal === 'sell' || h.signal === 'strong_sell' ? "#1a0a0a" : "#052e16",
                    color: h.signal === 'sell' || h.signal === 'strong_sell' ? "#f87171" : "#4ade80",
                  }}>
                    {h.signal.toUpperCase().replace(/_/g, " ")}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── PerformancePage ───────────────────────────────────────────────────────

type PerfTab = 'options' | 'stocks';

export default function PerformancePage({ subTab, onSubTabChange }: {
  subTab: string | null;
  onSubTabChange: (s: string | null) => void;
}) {
  const perfTab: PerfTab = subTab === 'stocks' ? 'stocks' : 'options';
  function setPerfTab(t: PerfTab) { onSubTabChange(t === 'options' ? null : t); }

  const [allTrades, setAllTrades] = useState<Trade[]>([]);
  const [allHoldings, setAllHoldings] = useState<StockHolding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<Period>(90);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [tradeData, holdingData] = await Promise.all([
        fetchTrades("closed"),
        fetchStockPortfolio("all"),
      ]);
      setAllTrades(tradeData);
      setAllHoldings(holdingData);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load trade history");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const trades = useMemo(() => filterByPeriod(allTrades, period), [allTrades, period]);

  const activeHoldings = useMemo(() =>
    allHoldings.filter((h) => h.status === 'holding'),
  [allHoldings]);

  const filteredSoldHoldings = useMemo(() => {
    const cutoff = Date.now() - period * 86400000;
    return allHoldings.filter((h) => {
      if (h.status === 'holding') return false;
      const soldAt = h.sold_at ? new Date(h.sold_at).getTime() : null;
      return soldAt != null && soldAt >= cutoff;
    });
  }, [allHoldings, period]);

  const stats = useMemo(() => {
    if (trades.length === 0) return null;
    const winners = trades.filter((t) => (t.realized_pnl ?? 0) > 0);
    const totalPnl = trades.reduce((s, t) => s + (t.realized_pnl ?? 0), 0);
    const avgCredit = winners.length > 0
      ? winners.reduce((s, t) => s + t.net_credit, 0) / winners.length
      : 0;
    return {
      winRate: (winners.length / trades.length) * 100,
      totalPnl,
      avgCredit,
      total: trades.length,
      winners: winners.length,
    };
  }, [trades]);

  // Compute P&L from prices directly, not stored values
  function holdingPnl(h: StockHolding): number {
    if (h.status !== 'holding') return h.realized_pnl ?? 0;
    const cp = h.current_price ?? h.buy_price;
    return (cp - h.buy_price) * h.quantity;
  }
  function holdingPnlPct(h: StockHolding): number {
    if (h.status !== 'holding') return h.realized_pnl_pct ?? 0;
    const cp = h.current_price ?? h.buy_price;
    return h.buy_price > 0 ? ((cp - h.buy_price) / h.buy_price) * 100 : 0;
  }

  const stockStats = useMemo(() => {
    const combined = [...activeHoldings, ...filteredSoldHoldings];
    if (combined.length === 0) return null;
    const totalPnl = combined.reduce((s, h) => s + holdingPnl(h), 0);
    const totalCost = combined.reduce((s, h) => s + h.total_cost, 0);
    const totalMarketValue = activeHoldings.reduce((s, h) => s + (h.current_price ?? h.buy_price) * h.quantity, 0);
    const winners = combined.filter((h) => holdingPnl(h) > 0);
    const avgReturn = combined.reduce((s, h) => s + holdingPnlPct(h), 0) / combined.length;
    return {
      totalPnl,
      totalCost,
      totalMarketValue,
      avgReturn,
      total: combined.length,
      active: activeHoldings.length,
      sold: filteredSoldHoldings.length,
      winners: winners.length,
      winRate: (winners.length / combined.length) * 100,
    };
  }, [activeHoldings, filteredSoldHoldings]);

  const PERIODS: Period[] = [30, 90, 365];

  return (
    <main style={{ maxWidth: 1440, margin: "0 auto", padding: "32px 40px 60px" }}>

      {/* Page header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <h2 style={{
            fontSize: 22, fontWeight: 800, fontFamily: "'Space Mono', monospace",
            color: "#f1f5f9", marginBottom: 6,
          }}>
            Performance
          </h2>
          <div style={{ fontSize: 12, color: "#475569", fontFamily: "monospace" }}>
            Historical P&amp;L and win rate analysis
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* Tab toggle */}
          <div style={{ display: "flex", gap: 4 }}>
            {([['options', 'Options'], ['stocks', 'Stocks']] as const).map(([key, label]) => (
              <button key={key} onClick={() => setPerfTab(key)} style={{
                padding: "7px 16px", borderRadius: 20, border: "1px solid",
                borderColor: perfTab === key ? "#1d4ed8" : "#1e293b",
                background: perfTab === key ? "#1d4ed820" : "transparent",
                color: perfTab === key ? "#60a5fa" : "#475569",
                fontSize: 12, cursor: "pointer", fontFamily: "monospace",
              }}>
                {label}
              </button>
            ))}
          </div>
          {/* Period toggle */}
          <div style={{ display: "flex", gap: 4 }}>
            {PERIODS.map((p) => (
              <button key={p} onClick={() => setPeriod(p)} style={{
                padding: "7px 16px", borderRadius: 20, border: "1px solid",
                borderColor: period === p ? "#1d4ed8" : "#1e293b",
                background: period === p ? "#1d4ed820" : "transparent",
                color: period === p ? "#60a5fa" : "#475569",
                fontSize: 12, cursor: "pointer", fontFamily: "monospace",
              }}>
                {p}D
              </button>
            ))}
          </div>
          <button onClick={load} disabled={loading} style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "9px 16px", borderRadius: 8, border: "1px solid #1e293b",
            background: "#0d1526", color: "#64748b", cursor: loading ? "default" : "pointer",
            fontSize: 12, fontFamily: "monospace",
          }}>
            <RefreshCw size={13} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
            {loading ? "LOADING..." : "REFRESH"}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          background: "#1a0a0a", border: "1px solid #7f1d1d", borderRadius: 12,
          padding: 20, marginBottom: 24, display: "flex", gap: 12, alignItems: "flex-start",
        }}>
          <AlertCircle size={18} color="#f87171" style={{ flexShrink: 0 }} />
          <div>
            <div style={{ color: "#f87171", fontWeight: 600, marginBottom: 4 }}>Failed to load performance data</div>
            <div style={{ color: "#7f1d1d", fontSize: 12, fontFamily: "monospace" }}>{error}</div>
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 28 }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} style={{
                height: 90, background: "#0a1628", borderRadius: 12,
                border: "1px solid #1e293b", animation: "pulse 1.5s ease infinite",
                opacity: 1 - i * 0.12,
              }} />
            ))}
          </div>
          <div style={{ height: 280, background: "#0a1628", borderRadius: 12, border: "1px solid #1e293b", animation: "pulse 1.5s ease infinite" }} />
        </>
      )}

      {/* Stats & table when loaded */}
      {!loading && !error && perfTab === 'options' && (
        <>
          {/* Stat cards */}
          {stats ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 28 }}>
              <StatCard
                icon={<Award size={18} />}
                label="WIN RATE"
                value={`${stats.winRate.toFixed(1)}%`}
                sub={`${stats.winners} / ${stats.total} trades`}
                color={stats.winRate >= 70 ? "#4ade80" : stats.winRate >= 50 ? "#facc15" : "#f87171"}
              />
              <StatCard
                icon={stats.totalPnl >= 0 ? <TrendingUp size={18} /> : <TrendingDown size={18} />}
                label="TOTAL P&L"
                value={`${stats.totalPnl >= 0 ? "+" : ""}$${stats.totalPnl.toFixed(0)}`}
                sub={`Last ${period} days`}
                color={stats.totalPnl >= 0 ? "#4ade80" : "#f87171"}
              />
              <StatCard
                icon={<BarChart2 size={18} />}
                label="AVG PREMIUM CAPTURED"
                value={`$${stats.avgCredit.toFixed(2)}`}
                sub="Per winning trade"
                color="#60a5fa"
              />
              <StatCard
                icon={<BarChart2 size={18} />}
                label="TOTAL TRADES"
                value={stats.total.toString()}
                sub={`In last ${period} days`}
                color="#facc15"
              />
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 28 }}>
              {[
                { icon: <Award size={18} />, label: "WIN RATE", color: "#4ade80" },
                { icon: <TrendingUp size={18} />, label: "TOTAL P&L", color: "#4ade80" },
                { icon: <BarChart2 size={18} />, label: "AVG PREMIUM CAPTURED", color: "#60a5fa" },
                { icon: <BarChart2 size={18} />, label: "TOTAL TRADES", color: "#facc15" },
              ].map((s) => (
                <StatCard key={s.label} icon={s.icon} label={s.label} value="—" color={s.color} />
              ))}
            </div>
          )}

          {stats && (
            <div style={{
              background: "#0a1628", border: "1px solid #1e293b", borderRadius: 12,
              padding: "20px 24px", marginBottom: 24,
            }}>
              <div style={{ fontSize: 11, color: "#475569", fontFamily: "monospace", letterSpacing: 1, marginBottom: 12 }}>
                WIN RATE BREAKDOWN · LAST {period} DAYS
              </div>
              <WinRateGauge rate={stats.winRate} />
              <div style={{ display: "flex", gap: 24, marginTop: 14, fontSize: 12, fontFamily: "monospace" }}>
                <span style={{ color: "#4ade80" }}>● Winners: {stats.winners}</span>
                <span style={{ color: "#f87171" }}>● Losers: {stats.total - stats.winners}</span>
                <span style={{ color: "#475569" }}>● Total: {stats.total}</span>
              </div>
            </div>
          )}

          {trades.length > 0 ? (
            <>
              <div style={{ fontSize: 11, color: "#475569", fontFamily: "monospace", letterSpacing: 1, marginBottom: 12 }}>
                CLOSED TRADES · LAST {period} DAYS
              </div>
              <ClosedTradesTable trades={trades} />
            </>
          ) : (
            <div style={{
              textAlign: "center", padding: "80px 0",
              border: "1px dashed #1e293b", borderRadius: 16,
            }}>
              <div style={{ fontSize: 48, marginBottom: 16, color: "#1e293b" }}>&#x25C8;</div>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 15, color: "#475569", marginBottom: 8 }}>
                No closed trades in the last {period} days
              </div>
              <div style={{ fontSize: 13, color: "#334155" }}>
                Close or expire positions from the Trades tab to see performance data here.
              </div>
            </div>
          )}
        </>
      )}

      {/* Stock holdings performance tab */}
      {!loading && !error && perfTab === 'stocks' && (
        <>
          {stockStats ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16, marginBottom: 28 }}>
              <StatCard
                icon={<TrendingUp size={18} />}
                label="MARKET VALUE"
                value={`$${stockStats.totalMarketValue.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                sub={`Cost: $${stockStats.totalCost.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                color="#60a5fa"
              />
              <StatCard
                icon={stockStats.totalPnl >= 0 ? <TrendingUp size={18} /> : <TrendingDown size={18} />}
                label="TOTAL P&L"
                value={`${stockStats.totalPnl >= 0 ? "+" : ""}$${stockStats.totalPnl.toFixed(0)}`}
                sub={`${stockStats.active} active · ${stockStats.sold} sold`}
                color={stockStats.totalPnl >= 0 ? "#4ade80" : "#f87171"}
              />
              <StatCard
                icon={<BarChart2 size={18} />}
                label="AVG RETURN"
                value={`${stockStats.avgReturn >= 0 ? "+" : ""}${stockStats.avgReturn.toFixed(1)}%`}
                sub="Across all positions"
                color={stockStats.avgReturn >= 0 ? "#4ade80" : "#f87171"}
              />
              <StatCard
                icon={<Award size={18} />}
                label="WIN RATE"
                value={`${stockStats.winRate.toFixed(1)}%`}
                sub={`${stockStats.winners} / ${stockStats.total} positions`}
                color={stockStats.winRate >= 70 ? "#4ade80" : stockStats.winRate >= 50 ? "#facc15" : "#f87171"}
              />
              <StatCard
                icon={<BarChart2 size={18} />}
                label="TOTAL POSITIONS"
                value={stockStats.total.toString()}
                sub={`${stockStats.active} active · ${stockStats.sold} sold`}
                color="#facc15"
              />
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16, marginBottom: 28 }}>
              {[
                { icon: <TrendingUp size={18} />, label: "MARKET VALUE", color: "#60a5fa" },
                { icon: <TrendingUp size={18} />, label: "TOTAL P&L", color: "#4ade80" },
                { icon: <BarChart2 size={18} />, label: "AVG RETURN", color: "#60a5fa" },
                { icon: <Award size={18} />, label: "WIN RATE", color: "#4ade80" },
                { icon: <BarChart2 size={18} />, label: "TOTAL POSITIONS", color: "#facc15" },
              ].map((s) => (
                <StatCard key={s.label} icon={s.icon} label={s.label} value="—" color={s.color} />
              ))}
            </div>
          )}

          {/* Active Holdings */}
          {activeHoldings.length > 0 && (
            <>
              <div style={{ fontSize: 11, color: "#475569", fontFamily: "monospace", letterSpacing: 1, marginBottom: 12 }}>
                ACTIVE HOLDINGS · {activeHoldings.length} POSITION{activeHoldings.length !== 1 ? "S" : ""}
              </div>
              <ActiveHoldingsTable holdings={activeHoldings} pnlFn={holdingPnl} pnlPctFn={holdingPnlPct} />
              <div style={{ height: 24 }} />
            </>
          )}

          {/* Sold Holdings */}
          {filteredSoldHoldings.length > 0 && (
            <>
              <div style={{ fontSize: 11, color: "#475569", fontFamily: "monospace", letterSpacing: 1, marginBottom: 12 }}>
                SOLD STOCKS · LAST {period} DAYS
              </div>
              <SoldStocksTable holdings={filteredSoldHoldings} />
            </>
          )}

          {activeHoldings.length === 0 && filteredSoldHoldings.length === 0 && (
            <div style={{
              textAlign: "center", padding: "80px 0",
              border: "1px dashed #1e293b", borderRadius: 16,
            }}>
              <div style={{ fontSize: 48, marginBottom: 16, color: "#1e293b" }}>&#x25C8;</div>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 15, color: "#475569", marginBottom: 8 }}>
                No stock holdings yet
              </div>
              <div style={{ fontSize: 13, color: "#334155" }}>
                Buy stocks from the Stock Picks tab to track performance here.
              </div>
            </div>
          )}
        </>
      )}
    </main>
  );
}
